import * as _ from 'underscore'
import { Enums } from '../common/enums'

import { ConvertHelper, DataProvider, Dictionary, TypeHelper } from '../../helpers'

import { IReferenceType, IJsonSerializable, IOpObject, ICopyable } from '../util/interfaces'
import { Utils } from '../util/utils'
import { OpDataList } from '../util'

import { OpArticle } from './op-article'
import { OpDepositoryElementData } from './op-depository-element-data'
import { OpDevice } from './op-device'
import { OpDeviceStateType } from './op-device-state-type'
import { OpDeviceTypeClass } from './op-device-type-class'
import { OpLocation } from './op-location'
import { OpPackage } from './op-package'
import { OpTask } from './op-task'

export class OpDepositoryElement
    implements IReferenceType, IOpObject, IJsonSerializable, ICopyable<OpDepositoryElement> {

    //#region Properties

    public idDepositoryElement: number
    public idLocation: number
    public idTask: number
    public idPackage: number
    public idArticle: number
    public idDeviceTypeClass: number
    public idDeviceStateType: number
    public serialNbr: number
    public startDate: Date
    public endDate: Date
    public removed: boolean
    public ordered: boolean
    public accepted: boolean
    public notes: string
    public externalSn: string
    public idReference: number

    //#endregion

    //#region Navigation Properties

    public location: OpLocation
    public task: OpTask
    public package: OpPackage
    public article: OpArticle
    public deviceTypeClass: OpDeviceTypeClass
    public deviceStateType: OpDeviceStateType
    public device: OpDevice

    //#endregion


    //#region Custom Properties

    public dataList: OpDataList<OpDepositoryElementData> = new OpDataList<OpDepositoryElementData>()

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idDepositoryElement
    }

    public set idObject(idObject: any) {
        this.idDepositoryElement = idObject
    }

    public dynamicValues: { Key: string; Value: any; }[]
    public customFormatString: string

    public toString(): string {
        return this.notes
    }

    //#endregion


    //#region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpDepositoryElement) {
            this.copy(<OpDepositoryElement>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion


    //IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdDepositoryElement
    }

    public getReferenceKey(): number {
        return this.idDepositoryElement
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //IOpChangeState

    public opState: Enums.OpChangeState

    //#endregion


    //#region IJsonSerializable

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idDepositoryElement = opObj.ID_DEPOSITORY_ELEMENT
            this.idLocation = opObj.ID_LOCATION
            this.idTask = opObj.ID_TASK
            this.idPackage = opObj.ID_PACKAGE
            this.idArticle = opObj.ID_ARTICLE
            this.idDeviceTypeClass = opObj.ID_DEVICE_TYPE_CLASS
            this.idDeviceStateType = opObj.ID_DEVICE_STATE_TYPE
            this.serialNbr = opObj.SERIAL_NBR
            this.startDate = opObj.START_DATE ? ConvertHelper.stringToDate(opObj.START_DATE) : null
            this.endDate = opObj.END_DATE ? ConvertHelper.stringToDate(opObj.END_DATE) : null
            this.removed = opObj.REMOVED
            this.ordered = opObj.ORDERED
            this.accepted = opObj.ACCEPTED
            this.notes = opObj.NOTES
            this.externalSn = opObj.EXTERNAL_SN
            this.idReference = opObj.ID_REFERENCE

            this.dynamicValues = opObj.dynamicValues

            this.opState = opObj.OpState

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map((data: any) => new OpDepositoryElementData(data)))
            }
        } else {
            this.idDepositoryElement = 0
            this.opState = Enums.OpChangeState.New
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_DEPOSITORY_ELEMENT: this.idDepositoryElement,
            ID_LOCATION: this.idLocation,
            ID_TASK: this.idTask,
            ID_PACKAGE: this.idPackage,
            ID_ARTICLE: this.idArticle,
            ID_DEVICE_TYPE_CLASS: this.idDeviceTypeClass,
            ID_DEVICE_STATE_TYPE: this.idDeviceStateType,
            SERIAL_NBR: this.serialNbr,
            START_DATE: this.startDate != null ? ConvertHelper.dateToString(this.startDate, { netFormat: true }) : null,
            END_DATE: this.endDate != null ? ConvertHelper.dateToString(this.endDate, { netFormat: true }) : null,
            REMOVED: this.removed,
            ORDERED: this.ordered,
            ACCEPTED: this.accepted,
            NOTES: this.notes,
            EXTERNAL_SN: this.externalSn,
            ID_REFERENCE: this.idReference,
            OpState: this.opState,

            dynamicValues: this.dynamicValues,

            DataList: this.dataList != null ? this.dataList.ToJSON() : null,
        }
    }

    //#endregion


    //#region ICopyable Implementation

    public copy(source: OpDepositoryElement, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return null
        }

        if (includeNavigationProperties) {
            if (source.article != null) {
                this.article = source.article.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (source.package != null) {
                this.package = source.package.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        if (includeCustomProperties) { }

        if (includeDataList && source.dataList != null) {
            this.dataList = source.dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeDynamicValues) {
            this.dynamicValues = TypeHelper.clone(source.dynamicValues, true)
        }

        this.idDepositoryElement = source.idDepositoryElement
        this.idLocation = source.idLocation
        this.idTask = source.idTask
        this.idPackage = source.idPackage
        this.idArticle = source.idArticle
        this.idDeviceTypeClass = source.idDeviceTypeClass
        this.idDeviceStateType = source.idDeviceStateType
        this.serialNbr = source.serialNbr
        this.startDate = source.startDate
        this.endDate = source.endDate
        this.removed = source.removed
        this.ordered = source.ordered
        this.accepted = source.accepted
        this.notes = source.notes
        this.externalSn = source.externalSn
        this.idReference = source.idReference

        this.opState = source.opState
    }

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpDepositoryElement {
        const newDepositoryElement = new OpDepositoryElement(null)

        newDepositoryElement.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newDepositoryElement
    }

    //#endregion

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpDepositoryElement[]): JQueryPromise<OpDepositoryElement[]> {
        return Utils
            .loadDataListNavigationProperties(dataProvider, list)
            .then(() => {
                return dataProvider.System
                    .getDictionaries([Enums.ReferenceType.IdArticle])
                    .then((dicts: Dictionary<Dictionary<IReferenceType>>) => {
                        if (dicts != null) {
                            const articleDict: Dictionary<OpArticle> = dicts.tryGetValue(Enums.ReferenceType.IdArticle) as Dictionary<OpArticle>

                            _.forEach(list, (depositoryElement: OpDepositoryElement) => {
                                if (depositoryElement.idArticle != null && articleDict != null) {
                                    depositoryElement.article = articleDict.tryGetValue(depositoryElement.idArticle)
                                }
                            })
                        }

                        return list
                    })
            })
    }

}

